import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import { getAdminAreaByIdApi } from "@/api/adminArea/adminAreaApi";
import {
  optimizeSelectValue,
  useGetAnalysisConfigMutation,
  useGetConfigurationByID,
  useSaveConfigurationMutation
} from "@/api/configuration/useConfigurationAPI";
import { fetchDatasetStream } from "@/api/dataset/fetchDatasetStream";
import {
  useGetDatasetDataMutation,
  useMutationDatasetMetadata
} from "@/api/dataset/useDatasetAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import SaveSuccessNotiContent from "@/components/spatialAnalysis/notification/saveConfigurationSuccess";
import { ConfigurationFormData } from "@/components/spatialAnalysis/saveConfigurationPopup";
import { MESSAGE } from "@/constants/message";
import { buildFilterFromResponse } from "@/helpers/filterHelper";
import { getDisplayErrorMessage } from "@/helpers/handleErrorResponse";
import { RootState } from "@/store";
import {
  captureWeightConfigBaseline,
  recalculateWeightPercentages,
  setDatasetHistory
} from "@/store/slice/datasetSlice";
import {
  setAdminAreaSettings as setGlobalAdminAreaSettings,
  setAdminAreaSettingsData as setGlobalAdminAreaSettingsData,
  setGlobalAdminArea,
  setSelectedAreaIds as setGlobalSelectedAreaIds
} from "@/store/slice/globalFilterSlice";
import { setSpatialState } from "@/store/slice/spatialSlice";
import {
  addWidget,
  setWidgetHistoryFromWidgets
} from "@/store/slice/widgetSlice";
import {
  EspatialAggregationType,
  SpatialAggregationType
} from "@/types/common";
import {
  Dataset,
  DatasetAttributeSchema,
  DatasetData,
  DatasetDetailResponse,
  DatasetHistory,
  DatasetIndices,
  DatasetMetadata
} from "@/types/dataset";
import {
  AnalysisConfigurationDetail,
  ConfigurationPayload,
  DatasetConfigAttributeResponse,
  DatasetConfigResponse
} from "@/types/project";
import { ECatchmentInputBy } from "@/types/spatial";
import { WidgetDefaultConfiguration } from "@/types/widget";
import {
  embedLegendConfigImages,
  mapLegendToStyleConfig,
  resolveMarkerAssetUrl
} from "@/utils/configurationMapper";
import { CustomError } from "@/utils/errors";
import {
  buildWidgetRestorePayloads,
  type WidgetRestoreTarget
} from "@/utils/widget";

import { useDataset } from "../map/useDataset";
import useHandleClearView from "../ui/useHandleClearView";
import useToast, { EToastType } from "../ui/useToast";
import {
  getDeletedDatasetsName,
  mapDatasetWeights,
  mapSpatialState
} from "../useAnalysisConfiguration";
import { useAppFeatures } from "../useAppFeatures";
import useConfigurationMappers from "./useConfigurationMapper";
import { buildDatasetFilter, getAggregationConfig } from "./utils";

/**
 * Resolve which persisted widgets belong to a given dataset instance.
 *
 * Configs saved with the widget refactor carry `widget_id`, shaped
 * `${datasetId}_${instance}_${n}`. A dataset that appears multiple times in one
 * project (same id, different filters/group_by) is disambiguated on load by its
 * 1-based duplicate index, so it claims only the widgets whose instance segment
 * matches. Older configs predate `widget_id`; for those we fall back to the
 * bare `dataset_id` and attach only to the first occurrence so duplicate
 * datasets don't each re-add the same widgets.
 */
export const getWidgetsForDatasetInstance = (
  widgets: WidgetDefaultConfiguration[],
  datasetId: string,
  instanceCount: number
): WidgetDefaultConfiguration[] => {
  if (widgets.some((widget) => widget.widget_id)) {
    const instancePrefix = `${datasetId}_${instanceCount}_`;
    return widgets.filter((widget) =>
      widget.widget_id?.startsWith(instancePrefix)
    );
  }

  return instanceCount === 1
    ? widgets.filter((widget) => widget.dataset_id === datasetId)
    : [];
};

/** Links a restored dataset's saved widgets to the instance they belong to. */
const registerWidgetRestoreTargets = (
  targets: Map<WidgetDefaultConfiguration, WidgetRestoreTarget>,
  datasetWidgets: WidgetDefaultConfiguration[],
  addedDataset: Dataset
) =>
  datasetWidgets.forEach((widget) =>
    targets.set(widget, {
      datasetInstanceId: addedDataset.data.id,
      defaultLabel: addedDataset.data.display_name ?? addedDataset.data.name
    })
  );

type UseConfigurationProps = {
  onSaveConfigSuccess?: () => void;
  onSaveConfigError?: (error: CustomError) => void;
  onOpenConfigSuccess?: (
    configurationDetailData: AnalysisConfigurationDetail
  ) => void;
  onOpenConfigError?: (error: CustomError) => void;
  setDuplicateNameOnSaveConfig?: (value: boolean) => void;
  onOpenProjectFromToast?: () => void;
};

export const useConfiguration = ({
  onSaveConfigSuccess,
  onSaveConfigError,
  onOpenConfigSuccess,
  onOpenConfigError,
  onOpenProjectFromToast
}: UseConfigurationProps) => {
  const dispatch = useDispatch();
  const {
    isRefactorAddDatasetEnabled,
    isAdminTreeEnabled,
    isBVTDataForCatchmentEnabled,
    isCoverageTypeEnabled,
    isCustomMapVisualizationLegendEnabled
  } = useAppFeatures();
  const {
    handleAddDataset,
    handleAddDeletedDataset,
    handleCreateDatasetHistory,
    generateDatasetData
  } = useDataset();
  const { showToast } = useToast();
  const {
    mappingAnalysisOutput,
    mappingAreaInput,
    mappingDataset,
    uploadPendingLegendImages,
    mappingAdmAreaIds,
    mappingWidgets
  } = useConfigurationMappers();
  const { handleCloseDetailView } = useHandleClearView();
  const { data: userProfileData } = useGetUserProfileAPI();
  const { latestAnalysisId } = useSelector((state: RootState) => state.spatial);

  const spatial = useSelector((state: RootState) => state.spatial);

  const handleOpenProjectFromToast = (data: AnalysisConfigurationDetail) => {
    if (!data?.id) return;
    handleCloseDetailView();
    onOpenProjectFromToast?.();
  };

  const { mutate: mutateSaveConfig } = useSaveConfigurationMutation({
    onMutate: () => {
      toast.dismiss();
      showToast({
        type: EToastType.LOADING,
        message: MESSAGE.SAVING_NEW_PROJECT
      });
    },
    onSuccess: (data, payload) => {
      toast.dismiss();
      showToast({
        type: EToastType.SUCCESS,
        message: (
          <SaveSuccessNotiContent
            title="Configuration successfully saved"
            description="You can check you project by clicking link below"
            buttonLabel="Open Saved Configuration"
            onOpen={() => handleOpenProjectFromToast(data)}
          />
        ),
        autoClose: false
      });
      // Need title and description for export summary analysis
      dispatch(
        setSpatialState({
          ...spatial,
          isEdit: true,
          currentConfigId: data.id,
          title: payload.name,
          description: payload.description
        })
      );

      onSaveConfigSuccess?.();
    },
    onError: (error) => {
      showToast({
        type: EToastType.ERROR,
        message: getDisplayErrorMessage(error)
      });
      onSaveConfigError?.(error);
    }
  });

  const { mutateAsync: getDatasetDataPost } = useGetDatasetDataMutation({
    timezone: userProfileData?.timezone
  });
  const { mutateAsync: getDatasetMetadata } = useMutationDatasetMetadata();

  /**
   * Handle saving the configuration.
   * @param basicInfo - The basic configuration form data.
   */
  const handleSaveConfiguration = async (basicInfo: ConfigurationFormData) => {
    // Upload any pending legend marker images first, then persist
    // their asset ids into the dataset payload. A failed upload aborts the
    // save so the config is never persisted with rows missing asset ids.
    let assetIdByImageUrl = new Map<string, string>();
    if (isCustomMapVisualizationLegendEnabled) {
      try {
        assetIdByImageUrl = await uploadPendingLegendImages();
      } catch {
        showToast({
          type: EToastType.ERROR,
          message: MESSAGE.CUSTOM_LEGEND_UPLOAD_ERROR_SAVE_CONFIG
        });
        return;
      }
    }
    const payload: ConfigurationPayload = {
      ...basicInfo,
      adm_area_ids: mappingAdmAreaIds(),
      datasets: mappingDataset(assetIdByImageUrl),
      area_input: mappingAreaInput(),
      analysis_output: mappingAnalysisOutput(),
      widget_config: { widgets: mappingWidgets() }
    };
    mutateSaveConfig(payload);
  };

  const getDatasetDataAndMetadata = async (
    reversedDatasets: DatasetConfigResponse[]
  ) => {
    const datasetsData: DatasetData[] = [];
    const datasetsDetail: DatasetMetadata[] = [];
    const datasetsDetailAggregated: DatasetMetadata[] = [];

    // Fetch dataset metadata and data sequentially
    for (const dataset of reversedDatasets) {
      const currentDatasetMetadata = await getDatasetMetadata({
        id: dataset.id
      });

      const filters = buildDatasetFilter(
        dataset.attributes,
        currentDatasetMetadata?.schema
      );

      // For H3 dataset with data source Bvarta, we need to get the aggregated schema
      if (
        currentDatasetMetadata?.spatial_aggregation_type ===
          EspatialAggregationType.H3 &&
        currentDatasetMetadata?.source === "bvt"
      ) {
        const attributeSchema = await getDatasetMetadata({
          id: currentDatasetMetadata?.id,
          h3Configuration: getAggregationConfig(dataset?.grid_config),
          show_id: false,
          group_by: dataset.group_by
        });

        datasetsDetailAggregated.push(attributeSchema);
      } else {
        datasetsDetailAggregated.push(currentDatasetMetadata);
      }

      const res = await fetchDatasetStream({
        datasetId: dataset.id,
        filters,
        timezone: userProfileData?.timezone,
        h3Configuration: getAggregationConfig(dataset?.grid_config),
        group_by: dataset?.group_by,
        generateDatasetData
      });

      datasetsDetail.push(currentDatasetMetadata);
      if (res?.rawDataset) datasetsData.push(res.rawDataset);
    }

    return { datasetsData, datasetsDetail, datasetsDetailAggregated };
  };

  /**
   * Arguments shared by both restore paths (open project, open analysis config)
   * when a saved dataset is re-added.
   */
  const buildRestoredDatasetArgs = async (
    dataset: DatasetConfigResponse,
    datasetMetadata: DatasetMetadata,
    datasetData: DatasetData,
    attributesSchema: DatasetAttributeSchema[]
  ) => {
    const datasetWeights = mapDatasetWeights(dataset, datasetMetadata?.schema);

    const legendStyleConfig = isCustomMapVisualizationLegendEnabled
      ? await embedLegendConfigImages(
          mapLegendToStyleConfig(dataset),
          resolveMarkerAssetUrl
        )
      : undefined;

    return {
      datasetMetadata: {
        ...datasetMetadata,
        display_name: dataset.display_name ?? dataset.name
      },
      datasetData,
      savedFilters: buildDatasetFilter(
        dataset.attributes,
        datasetMetadata?.schema
        // Filters are stored top-to-bottom in project config, but dataset
        // history grows bottom-to-top (each entry prepended). Reversing
        // here aligns the filter order with the history display order.
      ).reverse(),
      attributesSchema, // filtered metadata schema for h3 dataset
      h3Configuration: getAggregationConfig(dataset?.grid_config),
      group_by: dataset.group_by,
      weightValues: {
        // `dataset.weight` is 0 for projects saved before this field was
        // introduced. Fall back to the number of weighted attributes so
        // the weight panel initialises to a sensible non-zero value.
        weight: (
          dataset.weight ||
          Object.keys(datasetWeights).length ||
          1
        ).toString(),
        attributes: datasetWeights,
        include_scoring: dataset?.include_scoring
      },
      styleConfig: {
        colorFamily: dataset?.color_scale,
        opacity: dataset?.opacity,
        selectedCategory: dataset?.color_based,
        labelConfig: dataset?.label_config,
        colorSteps: dataset?.color_steps,
        rankConfig: {
          isEnabled: dataset.label_config?.enable_ranking ?? false,
          maxRanking: dataset.label_config?.max_ranking ?? 10
        },
        strokeConfig:
          dataset.spatial_aggregation_type === "line"
            ? {
                width: dataset?.stroke_width ?? 1,
                opacity: dataset?.opacity ?? 0.7
              }
            : undefined,
        // Spreading `undefined` is a no-op, so no `?? {}` fallback needed.
        ...legendStyleConfig
      },
      is_global_admin_area: dataset?.is_global_admin_area ?? false
    };
  };

  const handleDatasetOnOpenProject = async (
    datasets: DatasetConfigResponse[],
    widgets: WidgetDefaultConfiguration[]
  ) => {
    const reversedDatasets = [...datasets].reverse();

    const { datasetsData, datasetsDetail, datasetsDetailAggregated } =
      await getDatasetDataAndMetadata(
        reversedDatasets.filter((dataset) => dataset.available)
      );

    const duplicatedDatasetCount = new Map<string, number>();
    const widgetRestoreTargets = new Map<
      WidgetDefaultConfiguration,
      WidgetRestoreTarget
    >();
    // After all data and metadata are collected, add them to the map
    // index for reversedDatasets is used to align with datasetsData and datasetsDetail which are in reversed order, and for handle same dataset id with different filters or group_by in the same configuration
    for (const [index, dataset] of reversedDatasets.entries()) {
      const count = duplicatedDatasetCount.get(dataset.id) || 0;
      duplicatedDatasetCount.set(dataset.id, count + 1);
      const datasetWidgets = getWidgetsForDatasetInstance(
        widgets,
        dataset.id,
        count + 1
      );

      if (dataset.available) {
        const datasetMetadata: DatasetMetadata | undefined =
          datasetsDetail.find((d) => d.id === dataset.id);

        if (datasetMetadata) {
          const addedDataset = await handleAddDataset({
            ...(await buildRestoredDatasetArgs(
              dataset,
              datasetMetadata,
              datasetsData[index],
              datasetsDetailAggregated[index]?.schema ?? []
            )),
            datasetIndex: duplicatedDatasetCount.get(dataset.id)
          });

          registerWidgetRestoreTargets(
            widgetRestoreTargets,
            datasetWidgets,
            addedDataset
          );
        }
      } else {
        const deletedDatasetMetadata: DatasetDetailResponse = {
          id: dataset.id,
          name: dataset.name ?? "",
          display_name: dataset.display_name ?? "",
          country_id: "id", // arbitrary default value
          source: "bvt", // arbitrary default value
          spatial_aggregation_type:
            dataset.spatial_aggregation_type as SpatialAggregationType,
          short_description: "",
          published: false,
          created_at: "",
          updated_at: "",
          available: dataset.available,
          total_rows: 0,
          schema: []
        };
        const addedDeletedDataset = await handleAddDeletedDataset(
          deletedDatasetMetadata,
          count + 1
        );

        registerWidgetRestoreTargets(
          widgetRestoreTargets,
          datasetWidgets,
          addedDeletedDataset
        );
      }
    }

    // Widgets are restored after every dataset is on the map so the saved
    // top-to-bottom order is kept instead of being regrouped per dataset.
    buildWidgetRestorePayloads(widgets, widgetRestoreTargets).forEach(
      (payload) => dispatch(addWidget(payload))
    );
    // The backend config only stores `weight` per dataset/attribute, not `percentage`.
    // After all datasets are added with their weights, derive the display percentages
    // so the weight panel reflects the correct relative distribution.
    dispatch(recalculateWeightPercentages());
    // A loaded project carries a saved weight configuration, so it seeds the
    // saved baseline: a later dataset add/remove must surface the
    // weights-auto-adjusted alert without requiring another manual save.
    dispatch(captureWeightConfigBaseline());
    return { datasetsData, datasetsDetail };
  };

  const handleCatchmentDatasetOnOpenProject = async (
    catchmentDatasetId: string,
    catchmentDatasetAttributes: DatasetConfigAttributeResponse[]
  ) => {
    const catchmentFilter = catchmentDatasetAttributes
      .filter((attr) => attr.filter?.criteria)
      .map((attr) => ({
        ...buildFilterFromResponse(attr.filter!),
        filterId: attr.id.split("_")?.[0],
        attributeCode: attr?.attribute_code ?? ""
      }));
    return {
      catchmentDatasetDetail: await getDatasetMetadata({
        id: catchmentDatasetId
      }),
      catchmentDatasetData: await getDatasetDataPost({
        id: catchmentDatasetId,
        filter: catchmentFilter
      })
    };
  };

  // Multi-dataset variant: fetch metadata + raw data for every catchment
  // dataset in parallel. Failed fetches are logged and skipped so a single
  // bad dataset doesn't tear down the whole open-project / edit-input flow.
  const handleCatchmentDatasetsOnOpenProject = async (
    datasets: DatasetConfigResponse[]
  ) => {
    const results = await Promise.all(
      datasets.map(async (datasetConfig) => {
        try {
          const result = await handleCatchmentDatasetOnOpenProject(
            datasetConfig.id,
            datasetConfig.attributes ?? []
          );
          if (!result.catchmentDatasetDetail || !result.catchmentDatasetData) {
            return null;
          }
          return {
            detail: result.catchmentDatasetDetail,
            data: result.catchmentDatasetData,
            config: datasetConfig
          };
        } catch (error) {
          console.error(
            `Failed to fetch catchment dataset ${datasetConfig.id}:`,
            error
          );
          return null;
        }
      })
    );
    return results.filter((r): r is NonNullable<typeof r> => r !== null);
  };

  const handleAdmAreaOnOpenProject = async (adm_area_ids: string[]) => {
    if (adm_area_ids.length === 0) {
      return { adminAreaData: null };
    }

    const idsToFetch = isAdminTreeEnabled ? adm_area_ids : [adm_area_ids[0]];

    const adminAreaDetails = (
      await Promise.all(idsToFetch.map((id) => getAdminAreaByIdApi(id)))
    ).filter(Boolean);

    if (adminAreaDetails.length === 0) {
      return { adminAreaData: null };
    }

    // Use the first admin area detail to generate the selectValue,
    // as it represents the hierarchical selection.
    const selectValue = optimizeSelectValue(adminAreaDetails[0]);

    return {
      adminAreaData: {
        selectValue,
        geoData: {
          data: adminAreaDetails
        }
      }
    };
  };

  const getAreaInputAdmAreaIds = (
    configurationDetail: AnalysisConfigurationDetail
  ): string[] | undefined => {
    const areaInput = configurationDetail.area_input;
    return (
      areaInput?.adm_area_ids ??
      (areaInput?.adm_area_id ? [areaInput.adm_area_id] : undefined)
    );
  };

  const { mutateAsync: mutateOpenConfig, data: configurationDetailData } =
    useGetConfigurationByID({
      onMutate: () => {
        toast.dismiss();
        showToast({
          type: EToastType.LOADING,
          message: MESSAGE.LOADING_CONFIGURATION
        });
      },
      onSuccess: async (configurationDetail) => {
        const globalAdminAreaIds = configurationDetail.adm_area_ids;
        const areaInput = configurationDetail.area_input;
        // BFF normalizes responses: `datasets[]` is the single source of
        // truth for catchment dataset configs. The multi-dataset fetcher
        // pulls metadata + raw data for every entry.
        const allCatchmentDatasetConfigs = areaInput?.catchment?.datasets ?? [];
        const admAreaIds = getAreaInputAdmAreaIds(configurationDetail);
        const widgetConfig = configurationDetail?.widget_config;

        const { datasetsData, datasetsDetail } =
          isRefactorAddDatasetEnabled && configurationDetail.datasets
            ? await handleDatasetOnOpenProject(
                configurationDetail.datasets,
                widgetConfig?.widgets ?? []
              )
            : {};

        // Multi-dataset fetcher only fires when BVT flag is ON. When OFF, fall
        // back to the legacy singular fetch (only the first dataset entry).
        const catchmentDatasetsResult =
          isBVTDataForCatchmentEnabled &&
          isRefactorAddDatasetEnabled &&
          allCatchmentDatasetConfigs.length > 0
            ? await handleCatchmentDatasetsOnOpenProject(
                allCatchmentDatasetConfigs
              )
            : [];
        const {
          catchmentDatasetDetail: legacyCatchmentDatasetDetail,
          catchmentDatasetData: legacyCatchmentDatasetData
        } =
          !isBVTDataForCatchmentEnabled &&
          isRefactorAddDatasetEnabled &&
          allCatchmentDatasetConfigs[0]?.id
            ? await handleCatchmentDatasetOnOpenProject(
                allCatchmentDatasetConfigs[0].id,
                allCatchmentDatasetConfigs[0].attributes ?? []
              )
            : {
                catchmentDatasetDetail: undefined,
                catchmentDatasetData: undefined
              };
        // Keep singular fields populated from the first successful fetch so
        // any pre-existing reader (analytics, callbacks) still works.
        const catchmentDatasetDetail =
          catchmentDatasetsResult[0]?.detail ?? legacyCatchmentDatasetDetail;
        const catchmentDatasetData =
          catchmentDatasetsResult[0]?.data ?? legacyCatchmentDatasetData;

        const { adminAreaData } =
          isRefactorAddDatasetEnabled && admAreaIds
            ? await handleAdmAreaOnOpenProject(admAreaIds)
            : {};

        const { adminAreaData: globalAdminAreaData } = globalAdminAreaIds
          ? await handleAdmAreaOnOpenProject(globalAdminAreaIds)
          : {};

        showToast({
          message: MESSAGE.OPEN_PROJECT_SUCCESS,
          type: EToastType.SUCCESS
        });

        const deletedDatasets = getDeletedDatasetsName(
          configurationDetail.datasets
        );
        showToast({
          message: deletedDatasets
            ? `Dataset "${deletedDatasets}" cannot be loaded, please check the dataset from data management`
            : MESSAGE.OPEN_PROJECT_SUCCESS,
          type: deletedDatasets ? EToastType.WARNING : EToastType.SUCCESS
        });

        onOpenConfigSuccess?.({
          ...configurationDetail,
          ...(datasetsData && { datasetsData }),
          ...(datasetsDetail && { datasetsDetail }),
          ...(catchmentDatasetData && { catchmentDatasetData }),
          ...(catchmentDatasetDetail && { catchmentDatasetDetail }),
          ...(catchmentDatasetsResult.length > 0 && {
            catchmentDatasetsResult
          }),
          ...(adminAreaData && { adminAreaData }),
          ...(globalAdminAreaData && { globalAdminAreaData })
        });
      },
      onError: (error) => {
        console.error("Error fetching configuration:", error);
        showToast({
          type: EToastType.ERROR,
          message: MESSAGE.LOADING_CONFIGURATION_FAILED
        });
        onOpenConfigError?.(error);
      }
    });

  const { mutateAsync: mutateGetAnalysisConfig } = useGetAnalysisConfigMutation(
    {
      onSuccess: async (data, payload) => {
        const configurationDetail = JSON.parse(data.configuration);

        const globalAdminAreaIds = configurationDetail.adm_area_ids;
        const areaInput = configurationDetail.area_input;
        // BFF normalizes responses: `datasets[]` is the single source of
        // truth for catchment dataset configs. The multi-dataset fetcher
        // pulls metadata + raw data for every entry.
        const allCatchmentDatasetConfigs = areaInput?.catchment?.datasets ?? [];
        const admAreaIds = getAreaInputAdmAreaIds(configurationDetail);
        const datasets = configurationDetail.datasets;
        const reversedDatasets = [...datasets].reverse();
        const widgetConfig = configurationDetail?.widget_config;

        const datasetHistory: DatasetHistory = {
          datasets: {},
          rawDatasets: {},
          visualizationOrder: []
        };
        const datasetIndices: DatasetIndices = {};
        const duplicatedDatasetCount = new Map<string, number>();
        const widgetRestoreTargets = new Map<
          WidgetDefaultConfiguration,
          WidgetRestoreTarget
        >();

        const { datasetsData, datasetsDetail, datasetsDetailAggregated } =
          await getDatasetDataAndMetadata(reversedDatasets);

        // index for reversedDatasets is used to align with datasetsData and datasetsDetail which are in reversed order, and for handle same dataset id with different filters or group_by in the same configuration
        for (const [index, dataset] of reversedDatasets.entries()) {
          const count = duplicatedDatasetCount.get(dataset.id) || 0;
          duplicatedDatasetCount.set(dataset.id, count + 1);
          const datasetWidgets = getWidgetsForDatasetInstance(
            widgetConfig?.widgets ?? [],
            dataset.id,
            count + 1
          );
          const datasetMetadata: DatasetMetadata | undefined =
            datasetsDetail.find((d) => d.id === dataset.id);

          if (datasetMetadata) {
            const addedDataset = await handleCreateDatasetHistory({
              ...(await buildRestoredDatasetArgs(
                dataset,
                datasetMetadata,
                datasetsData[index],
                datasetsDetailAggregated[index]?.schema ?? []
              )),
              datasetHistory,
              datasetIndices
            });

            registerWidgetRestoreTargets(
              widgetRestoreTargets,
              datasetWidgets,
              addedDataset
            );
          }
        }

        dispatch(setDatasetHistory(datasetHistory));
        // The analysis config restores into history, not into live widgets.
        dispatch(
          setWidgetHistoryFromWidgets(
            buildWidgetRestorePayloads(
              widgetConfig?.widgets ?? [],
              widgetRestoreTargets
            )
          )
        );
        // The analysis config payload only carries `weight` values, not `percentage`.
        // After the dataset history is committed to the store, compute percentages
        // from the loaded weights so the weight panel shows the correct distribution.
        dispatch(recalculateWeightPercentages());
        // Restoring an analysis config seeds the saved-weight baseline, same
        // as opening a project.
        dispatch(captureWeightConfigBaseline());

        // Multi-dataset fetcher only fires when BVT flag is ON. When OFF, fall
        // back to the legacy singular fetch.
        const catchmentDatasetsResult =
          isBVTDataForCatchmentEnabled && allCatchmentDatasetConfigs.length > 0
            ? await handleCatchmentDatasetsOnOpenProject(
                allCatchmentDatasetConfigs
              )
            : [];
        const {
          catchmentDatasetDetail: legacyCatchmentDatasetDetail,
          catchmentDatasetData: legacyCatchmentDatasetData
        } =
          !isBVTDataForCatchmentEnabled && allCatchmentDatasetConfigs[0]?.id
            ? await handleCatchmentDatasetOnOpenProject(
                allCatchmentDatasetConfigs[0].id,
                allCatchmentDatasetConfigs[0].attributes ?? []
              )
            : {
                catchmentDatasetDetail: undefined,
                catchmentDatasetData: undefined
              };
        const catchmentDatasetDetail =
          catchmentDatasetsResult[0]?.detail ?? legacyCatchmentDatasetDetail;
        const catchmentDatasetData =
          catchmentDatasetsResult[0]?.data ?? legacyCatchmentDatasetData;

        const { adminAreaData } = admAreaIds
          ? await handleAdmAreaOnOpenProject(admAreaIds)
          : {};

        const { adminAreaData: globalAdminAreaData } = globalAdminAreaIds
          ? await handleAdmAreaOnOpenProject(globalAdminAreaIds)
          : {};

        const configResult = {
          ...configurationDetail,
          ...(datasetsData && { datasetsData }),
          ...(datasetsDetail && { datasetsDetail }),
          ...(catchmentDatasetData && { catchmentDatasetData }),
          ...(catchmentDatasetDetail && { catchmentDatasetDetail }),
          ...(catchmentDatasetsResult.length > 0 && {
            catchmentDatasetsResult
          }),
          ...(adminAreaData && { adminAreaData }),
          ...(globalAdminAreaData && { globalAdminAreaData })
        };

        // Opening an analysis result must not draw input geometry on the map
        // (matches the fresh-run path). State is still restored below via
        // setSpatialState so Edit Input / closing the result popup can redraw.
        const spatialData = mapSpatialState(
          configResult,
          () => undefined,
          payload?.analysisId ?? spatial.latestAnalysisId ?? undefined,
          isAdminTreeEnabled,
          isBVTDataForCatchmentEnabled,
          isCoverageTypeEnabled
        );

        // Preserve local polygon state on post-analysis refetch only:
        // BE doesn't persist polygon for catchment+BY_POLYGON, so the BE
        // response returns spatial state without it. `latestAnalysisId`
        // gates this to the post-analysis flow (it's set when an analysis
        // has been started in the current session).
        const shouldPreservePolygonCatchment =
          !!latestAnalysisId &&
          isBVTDataForCatchmentEnabled &&
          spatial.catchmentSettings?.catchmentInputBy ===
            ECatchmentInputBy.BY_POLYGON &&
          Object.keys(spatial.polygonSettings ?? {}).length > 0;

        dispatch(
          setSpatialState({
            ...spatialData,
            ...(shouldPreservePolygonCatchment && {
              polygonSettings: spatial.polygonSettings,
              catchmentSettings: spatial.catchmentSettings,
              spatialConfig: spatial.spatialConfig
            })
          })
        );

        if (isAdminTreeEnabled) {
          if (configResult.globalAdminAreaData?.selectValue?.lv0) {
            dispatch(
              setGlobalAdminAreaSettings({
                lv0: configResult.globalAdminAreaData?.selectValue?.lv0
              })
            );
          }
          dispatch(
            setGlobalAdminAreaSettingsData(
              configResult.globalAdminAreaData?.geoData ?? null
            )
          );
          dispatch(setGlobalSelectedAreaIds(configResult.adm_area_ids || []));
        } else {
          dispatch(setGlobalAdminArea(configResult.globalAdminAreaData));
        }
      },
      onError: (error) => {
        console.error("Error fetching analysis configuration:", error);
        showToast({
          type: EToastType.ERROR,
          message: MESSAGE.LOADING_CONFIGURATION_FAILED
        });
      }
    }
  );

  return {
    mutateSaveConfig: handleSaveConfiguration,
    mutateOpenConfig,
    mutateGetAnalysisConfig,
    configurationDetailData // TODO: to be deleted after refactor
  };
};
